import MUIDataTable, { DisplayData, MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables'
import React from 'react'
import { rowsDeleted, SelectedRows } from 'src/types/Datatable'
import { PaginationDetails } from 'src/types/Paginate'

interface Props<T = any> {
  title?: string
  selectableRows?: 'none' | 'multiple' | 'single' | undefined
  list: T[]
  onClick?(obj: T): void
  serverSide?: boolean
  download?: boolean
  filter?: boolean
  viewColumns?: boolean
  count?: number
  tableColumns: MUIDataTableColumn[]
  customToolbarSelect?(selectedRows: SelectedRows, displayData: DisplayData, setSelectedRows: (rows: number[]) => void): any
  customToolbar?(data: { displayData: DisplayData }): any
  onRowsDelete?(data: rowsDeleted): any
  tableState?: PaginationDetails<T>
  setTableState?: React.Dispatch<React.SetStateAction<PaginationDetails<T>>>
}

const CustomDatatable: React.FC<Props> = ({
  title = '',
  download = true,
  filter = true,
  viewColumns = true,
  selectableRows = 'none',
  list,
  onClick,
  serverSide = false,
  tableColumns,
  onRowsDelete,
  count = 0,
  customToolbarSelect,
  customToolbar,
  tableState,
  setTableState,
}) => {
  const [defaultSelectableRows, setDefaultSelectableRows] = React.useState<'multiple' | 'single' | 'none' | undefined>('none')

  const handleClick = React.useCallback(
    (_rowData, rowMeta) => {
      if (onClick) onClick(list[rowMeta.dataIndex].id ? list[rowMeta.dataIndex].id : list[rowMeta.dataIndex]._id)
    },
    [onClick, list]
  )

  React.useEffect(() => {
    setDefaultSelectableRows(selectableRows)
  }, [selectableRows])

  const handleDownload = (buildHead: (columns: any) => string, buildBody: (data: any) => string, columns: any, data: any) => {
    const updatedData = [...data]
    data.forEach((columnDataList: any, i: number) => {
      columnDataList.data.forEach((columnData: any, index: number) => {
        if (typeof columnData !== 'string') {
          updatedData[i].data[index] = columns[index].customBodyRender(columnData)
        }
      })
    })

    return buildHead(columns) + buildBody(updatedData)
  }

  const options: MUIDataTableOptions = {
    filterType: 'textField',
    responsive: 'standard',
    search: false,
    print: false,
    page: tableState ? tableState.page : 0,
    download: download,
    filter: filter,
    viewColumns: viewColumns,
    selectableRows: defaultSelectableRows,
    rowsSelected: [],
    draggableColumns: { enabled: true, transitionTime: 100 },
    onRowClick: handleClick,
    serverSide: serverSide,
    count: count,
    onRowsDelete,
    onTableChange: (action, tableState) => {
      const actions: string[] = ['changePage', 'sort', 'changeRowsPerPage']
      if (action === 'viewColumnsChange') {
        if (tableState.columns.map((column) => column.display).indexOf('true') === -1) {
          setDefaultSelectableRows('none')
        } else {
          setDefaultSelectableRows(selectableRows)
        }
      }
      if (actions.indexOf(action) > -1 && setTableState) {
        setTableState((prev) => ({
          ...prev,
          page: tableState.page,
          rowsPerPage: tableState.rowsPerPage,
          sort: tableState.sortOrder.name,
          order: tableState.sortOrder.direction,
        }))
      }
    },
    customToolbarSelect,
    selectableRowsHeader: false,
    customToolbar,
    onDownload: handleDownload,
  }

  return <MUIDataTable title={title} data={list || []} columns={tableColumns} options={options} />
}

export default CustomDatatable
